import { ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  historyAPI,
  HistorySectionListResponse,
  HistoryArticleCard,
} from '../../services/api';
import {
  ArrowLeft,
  Loader2,
  BookOpen,
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';
import Pagination from '../../components/Pagination';

function ArticleRow({ article }: { article: HistoryArticleCard }): ReactElement {
  const articleHref = historyAPI.getArticleHtmlUrl(article.id);
  return (
    <div className="group relative bg-stone-700/50 border border-stone-700 rounded-lg p-3.5 sm:p-4 hover:bg-stone-700 hover:border-stone-600 transition-all duration-200">
      <div className="flex items-start justify-between gap-3 sm:gap-4">
        <a href={articleHref} className="flex-1 min-w-0 block">
          <h3
            className="text-sm sm:text-base font-semibold text-stone-200 group-hover:text-white transition-colors mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {article.headline}
          </h3>
          {article.summary && (
            <p className="text-xs sm:text-sm text-stone-500 mb-2 line-clamp-2">
              {article.summary}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-stone-500">
            {article.era && (
              <>
                <span className="text-amber-500">{article.era}</span>
                <span>&middot;</span>
              </>
            )}
            {article.country && (
              <>
                <span>{article.country}</span>
                <span>&middot;</span>
              </>
            )}
            <span>{article.time_ago}</span>
          </div>
        </a>
        <BookmarkButton
          itemType="history"
          itemId={article.id}
          title={article.headline}
          subtitle={article.era ?? undefined}
          url={articleHref}
          size="sm"
          className="text-stone-500 hover:text-amber-400 hover:bg-stone-700/50"
        />
      </div>
    </div>
  );
}

export default function HistorySectionList(): ReactElement {
  const { section } = useParams<{ section: string }>();
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading, error } = useQuery<HistorySectionListResponse>({
    queryKey: ['history-section', section, page],
    queryFn: async () => {
      const response = await historyAPI.getSectionArticles(section || '', {
        limit,
        offset: page * limit,
      });
      return response.data;
    },
    enabled: !!section,
    staleTime: 2 * 60 * 1000,
  });

  const batchItems = (data?.articles ?? []).map((a) => ({
    itemType: 'history' as const,
    itemId: a.id,
  }));

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-stone-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Back Link */}
        <Link
          to="/history"
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-300 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to History
        </Link>

        {/* Header */}
        <header className="mb-6 sm:mb-8">
          <h1
            className="text-xl sm:text-2xl font-bold text-stone-200"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {data?.name || section?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'History'}
          </h1>
        </header>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
            <p className="text-sm text-stone-500">Loading articles...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <BookOpen className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-stone-300 mb-2">Failed to load articles</h3>
            <p className="text-sm text-stone-500">Please try again later</p>
          </div>
        )}

        {/* Articles */}
        {data && !isLoading && (
          <>
            <div className="space-y-2.5 sm:space-y-3">
              {data.articles.map((article) => (
                <ArticleRow key={article.id} article={article} />
              ))}
            </div>

            {data.articles.length === 0 && (
              <div className="text-center py-20">
                <BookOpen className="w-8 h-8 text-stone-600 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-stone-400 mb-2">No articles yet</h3>
                <p className="text-sm text-stone-500">Check back later for new content</p>
              </div>
            )}

            {/* Pagination */}
            <Pagination
              page={page}
              totalPages={Math.ceil(data.total_count / limit)}
              onPageChange={setPage}
              hasMore={data.has_more}
              variant="dark"
            />
          </>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
